import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, FileText, Search, Filter, Eye, Edit, X, Check } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandling';
import KebabMenu from '../ui/KebabMenu';
import QuestionReviewModal from './QuestionReviewModal';

const QuestionsForReview = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    loadQuestionsForReview();
  }, []);

  const loadQuestionsForReview = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getQuestionsForReview({
        subject: selectedSubject,
        status: selectedStatus,
        search: searchTerm
      });

      // Handle paginated response
      if (response.data && response.data.content) {
        setQuestions(response.data.content);
      } else {
        setQuestions(response.data || []);
      }
    } catch (error) {
      console.error('Error loading questions for review:', error);
      showErrorToast(error, 'Failed to load questions for review');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReview = (question) => {
    setSelectedQuestion(question);
    setIsReviewModalOpen(true);
  };

  const handleReviewComplete = () => {
    setIsReviewModalOpen(false);
    setSelectedQuestion(null);
    loadQuestionsForReview();
    showSuccessToast('Question review submitted successfully!');
  };

  const handleApproveQuestion = async (question) => {
    try {
      // await superAdminAPI.approveQuestion(question.id);
      showSuccessToast('Question approved successfully!');
      loadQuestionsForReview();
    } catch (error) {
      console.error('Error approving question:', error);
      showErrorToast(error, 'Failed to approve question');
    }
  };

  const handleRejectQuestion = async (question) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      try {
        // await superAdminAPI.rejectQuestion(question.id, reason);
        showSuccessToast('Question rejected with feedback!');
        loadQuestionsForReview();
      } catch (error) {
        console.error('Error rejecting question:', error);
        showErrorToast(error, 'Failed to reject question');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Review': 'bg-yellow-100 text-yellow-800',
      'In Review': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  const getDifficultyBadge = (difficulty) => {
    const difficultyConfig = {
      'Easy': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Hard': 'bg-red-100 text-red-800'
    };
    return difficultyConfig[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const filteredQuestions = questions.filter(question => {
    const matchesSearch = !searchTerm ||
                         (question.questionText && question.questionText.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (question.subject && question.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (question.topic && question.topic.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSubject = !selectedSubject || question.subject === selectedSubject;
    const matchesStatus = !selectedStatus || question.status === selectedStatus;
    return matchesSearch && matchesSubject && matchesStatus;
  });

  const subjects = [...new Set(questions.map(q => q.subject).filter(Boolean))];
  const statuses = [...new Set(questions.map(q => q.status).filter(Boolean))];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questions for Review</h1>
          <p className="text-gray-600 mt-1">Review and approve questions created by content generators</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {filteredQuestions.filter(q => q.status === 'Pending Review').length} pending review
          </span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search questions by text, subject, or topic..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Questions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <CardLoadingSpinner />
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No questions for review</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedSubject || selectedStatus
                ? 'Try adjusting your search criteria or filters.'
                : 'All questions have been reviewed or no questions are assigned to you.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject/Topic
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuestions.map((question) => (
                  <tr key={question.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {question.questionText}
                        </div>
                        {question.tags && question.tags.length > 0 && (
                          <div className="text-sm text-gray-500">
                            Tags: {question.tags.slice(0, 2).join(', ')}
                            {question.tags.length > 2 && ` +${question.tags.length - 2} more`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{question.subject}</div>
                      <div className="text-sm text-gray-500">{question.topic}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyBadge(question.difficultyLevel)}`}>
                        {question.difficultyLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(question.status)}`}>
                        {question.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{question.createdBy}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {question.assignedTo ? 'Assigned' : 'Unassigned'}
                      </div>
                      {question.updatedAt && (
                        <div className="text-xs text-gray-500">
                          {new Date(question.updatedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleStartReview(question)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Review Question"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleApproveQuestion(question)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Approve Question"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRejectQuestion(question)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Reject Question"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {isReviewModalOpen && selectedQuestion && (
        <QuestionReviewModal
          isOpen={isReviewModalOpen}
          question={selectedQuestion}
          onClose={() => setIsReviewModalOpen(false)}
          onReviewComplete={handleReviewComplete}
        />
      )}
    </div>
  );
};

export default QuestionsForReview;
